import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import api, { TOKEN_KEY } from '../api/client'
import type { User } from '../api/types'
import { AuthContext } from './useAuth'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Always attempt /me: even with no (or an expired) access token, a valid
    // refresh cookie lets the response interceptor silently mint a new one.
    api
      .get<User>('/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ token: string; user: User }>('/auth/login', { email, password })
    localStorage.setItem(TOKEN_KEY, res.data.token)
    setUser(res.data.user)
  }, [])

  const register = useCallback(async (email: string, password: string, inviteToken: string) => {
    const res = await api.post<{ token: string; user: User }>('/auth/register', {
      email,
      password,
      inviteToken,
    })
    localStorage.setItem(TOKEN_KEY, res.data.token)
    setUser(res.data.user)
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      localStorage.removeItem(TOKEN_KEY)
      setUser(null)
    }
  }, [])

  const value = useMemo(
    () => ({ user, loading, login, register, logout }),
    [user, loading, login, register, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
